import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { InputNumber } from "primereact/inputnumber";
import { Checkbox } from "primereact/checkbox";
import api from "../../api/axios";
import { useAuth } from "../../context/AuthContext";
import SupplierService from "../../services/SupplierService";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { useNavigate, useBlocker } from "react-router-dom";
import { Skeleton } from "primereact/skeleton";
import WarehouseService from "../../services/WarehouseService";

export default function SupplierReturnCreatePage() {
  const navigate = useNavigate();
  const toast = useRef(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const [supplier, setSupplier] = useState({
    id: 0,
    name: "",
    balance: 0,
  });
  const [warehouseOptions, setWarehouseOptions] = useState([]);

  const [productQuery, setProductQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [showList, setShowList] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const inputRef = useRef(null);
  const quantityRef = useRef(null);
  const priceRef = useRef(null);
  const totalPriceRef = useRef(null);
  const discountRef = useRef(null);
  const vatRef = useRef(null);

  const [newLine, setNewLine] = useState({
    product: null,
    quantity: 1,
    unitPrice: null,
    unitPriceIsVatIncluded: false,
    discountRate: 0,
    vatRate: 0,
  });

  const [lines, setLines] = useState([]);
  const [isAddFocused, setIsAddFocused] = useState(false);

  const [returnHeader, setReturnHeader] = useState({
    invoiceNo: "IADE-" + Math.floor(Math.random() * 100000),
    returnDate: new Date(),
    warehouseId: null,
    warehouseCode: "MERKEZ",
    currencyCode: "TRY",
    description: "",
  });

  const location = useLocation();
  const { user: authUser } = useAuth();

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && !isSubmitting && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty && !isSubmitting) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty, isSubmitting]);

  useEffect(() => {
    const sid = location?.state?.supplierId;
    const sname = location?.state?.supplierName;
    if (!sid && !sname) {
      setLoading(false);
      return;
    }

    setSupplier((prev) => ({
      id: sid ?? prev.id,
      name: sname ?? prev.name,
      balance: prev.balance,
    }));

    if (sid) {
      setLoading(true);
      SupplierService.getSupplierById(sid).then(sres => {
        if (sres) {
          setSupplier(prev => ({ ...prev, balance: sres.balance }));
        }
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [location?.state]);

  useEffect(() => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, []);

  const focusAndSelect = (ref) => {
    if (!ref.current) return;
    const inputEl = ref.current.getInput ? ref.current.getInput() : ref.current.input?.current || ref.current.input;
    if (inputEl && inputEl.select) {
      inputEl.focus();
      inputEl.select();
    } else {
      ref.current.focus?.();
    }
  };

  const selectProduct = (product) => {
    if (!product) return;
    setNewLine((prev) => ({
      ...prev,
      product,
      unitPrice: product.defaultPurchasePrice ?? 0,
      vatRate: (product.vatRate === null || product.vatRate === undefined) ? 0 : product.vatRate,
      unit: product.unit || "",
      quantity: 1,
      unitPriceIsVatIncluded: product.isVatIncluded || false,
    }));
    setProductQuery(product.name ?? "");
    setProducts([]);
    setShowList(false);
    setTimeout(() => {
      focusAndSelect(quantityRef);
    }, 400);
  };

  const searchProducts = async (query) => {
    try {
      const res = await api.get("/product/autocomplete", {
        params: { query, take: 20 },
      });
      setProducts(res.data || []);
      setShowList(true);
      setHighlightIndex(0);
    } catch (e) {
      setProducts([]);
    }
  };

  const handleNewLineChange = (field, value) => {
    setNewLine((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddLine = () => {
    if (!newLine.product) return;
    setIsDirty(true);
    setLines((prev) => [
      ...prev,
      {
        id: Date.now(),
        productId: newLine.product.id,
        productName: newLine.product.name,
        unit: newLine.unit || "",
        quantity: newLine.quantity,
        unitPrice: newLine.unitPrice,
        unitPriceIsVatIncluded: newLine.unitPriceIsVatIncluded,
        discountRate: newLine.discountRate,
        vatRate: newLine.vatRate,
      },
    ]);
    setNewLine({
      product: null,
      quantity: 1,
      unitPrice: 0,
      unitPriceIsVatIncluded: false,
      discountRate: 0,
      vatRate: 0,
      unit: "",
    });
    setProductQuery("");
    setShowList(false);
    setProducts([]);
    setIsAddFocused(false);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const openDeleteConfirm = (event, id) => {
    confirmPopup({
      target: event.currentTarget,
      message: "Bu satırı silmek istediğinize emin misiniz?",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sil",
      rejectLabel: "İptal",
      acceptClassName: "p-button-danger",
      accept: () => {
        setIsDirty(true);
        setLines((prev) => prev.filter((x) => x.id !== id));
      },
    });
  };

  const totals = useMemo(() => {
    let gross = 0;
    let discountTotal = 0;
    let vatTotal = 0;
    let totalQuantity = 0;
    let grandTotal = 0;

    lines.forEach((l) => {
      const qty = l.quantity || 0;
      const unitPrice = l.unitPrice || 0;
      const vatRate = l.vatRate || 0;
      const discountRate = l.discountRate || 0;
      const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;

      if (l.unitPriceIsVatIncluded) {
        const unitPriceExcl = vatRate > 0 ? unitPrice / (1 + vatRate / 100) : unitPrice;
        const lineGrossExcl = unitPriceExcl * qty;
        const discountExcl = round2(lineGrossExcl * (discountRate / 100));
        const netExclAfterDiscount = round2(lineGrossExcl - discountExcl);
        const vat = round2(netExclAfterDiscount * (vatRate / 100));
        const lineTotalIncl = round2(netExclAfterDiscount + vat);

        gross += round2(lineGrossExcl);
        discountTotal += discountExcl;
        vatTotal += vat;
        grandTotal += lineTotalIncl;
        totalQuantity += qty;
      } else {
        const lineGrossExcl = unitPrice * qty;
        const discountExcl = round2(lineGrossExcl * (discountRate / 100));
        const netExclAfterDiscount = round2(lineGrossExcl - discountExcl);
        const vat = round2(netExclAfterDiscount * (vatRate / 100));
        const lineTotalIncl = round2(netExclAfterDiscount + vat);

        gross += round2(lineGrossExcl);
        discountTotal += discountExcl;
        vatTotal += vat;
        grandTotal += lineTotalIncl;
        totalQuantity += qty;
      }
    });

    return {
      gross,
      discountTotal,
      vatTotal,
      netTotal: grandTotal,
      lineCount: lines.length,
      totalQuantity,
    };
  }, [lines]);

  const handleHeaderChange = (field, value) => {
    setIsDirty(true);
    setReturnHeader((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const formatDateOnly = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const buildDtoPayload = () => ({
    supplierId: supplier.id,
    invoiceNo: returnHeader.invoiceNo,
    returnDate: formatDateOnly(returnHeader.returnDate),
    warehouseId: returnHeader.warehouseId,
    warehouseCode: returnHeader.warehouseCode,
    currencyCode: returnHeader.currencyCode,
    description: returnHeader.description,
    lines: lines.map((l) => ({
      productId: l.productId,
      quantity: l.quantity,
      unit: l.unit,
      unitPrice: l.unitPrice,
      unitPriceIsVatIncluded: !!l.unitPriceIsVatIncluded,
      discountRate: l.discountRate ?? 0,
      vatRate: l.vatRate ?? 0,
    })),
  });

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const payload = buildDtoPayload();
      const res = await SupplierService.saveSupplierReturn(payload);

      if (res.success) {
        setIsDirty(false);
        toast.current.show({
          severity: "success",
          summary: "Başarılı",
          detail: "İade işlemi başarıyla kaydedildi.",
          life: 3000,
        });
        setTimeout(() => {
          navigate(`/suppliers/detail/${supplier.id}`);
        }, 1500);
      } else {
        toast.current.show({
          severity: "error",
          summary: "Hata",
          detail: res.message || "İşlem gerçekleştirilemedi.",
          life: 3000
        });
      }
    } catch (err) {
      toast.current.show({
        severity: "error",
        summary: "Hata",
        detail: "İade kaydedilirken hata oluştu.",
        life: 3000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    WarehouseService.getAll().then(res => {
      if (res.success) {
        const options = res.data.map(w => ({ label: w.name, value: w.id }));
        setWarehouseOptions(options);
        if (options.length > 0) {
          setReturnHeader(prev => ({ ...prev, warehouseId: options[0].value }));
        }
      }
    });
  }, []);

  if (!mounted || loading) {
    return (
      <div className="p-2 md:p-3 space-y-4">
        <Skeleton height="4rem" className="rounded-xl" />
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-2"><Skeleton height="25rem" className="rounded-2xl" /></div>
          <div className="md:col-span-8"><Skeleton height="25rem" className="rounded-2xl" /></div>
          <div className="md:col-span-2"><Skeleton height="25rem" className="rounded-2xl" /></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 md:p-3 space-y-4 animate-fade-in bg-[#f8fafc] min-h-screen">
      <Toast ref={toast} appendTo={document.body}  appendTo={mounted ? document.body : "self"} />
      <ConfirmPopup appendTo={document.body} />

      <Dialog
        header="Kaydedilmemiş Değişiklikler"
        visible={blocker.state === "blocked"}
        style={{ width: '450px' }}
        modal
        appendTo={document.body}
        onHide={() => blocker.reset()}
        footer={
          <div className="flex justify-end gap-2">
            <Button label="İptal" icon="pi pi-times" onClick={() => blocker.reset()} className="p-button-text p-button-secondary" />
            <Button label="Hayır" icon="pi pi-trash" onClick={() => blocker.proceed()} className="p-button-text p-button-danger" />
            <Button label="Evet" icon="pi pi-check" onClick={handleSave} autoFocus className="!bg-blue-600 border-none" />
          </div>
        }
      >
        <span>Değişiklikleriniz kaydedilsin mi?</span>
      </Dialog>

      <div className="bg-white shadow-lg shadow-slate-200/50 border border-slate-200 rounded-2xl p-4 md:p-5">
        <div className="flex flex-col md:flex-row items-center justify-between mb-6 gap-6">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <div className="!bg-orange-600 p-3 rounded-xl shadow-md shadow-orange-500/20">
                <i className="pi pi-refresh text-white text-xl"></i>
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-black text-slate-900 uppercase tracking-tight">Tedarikçiye İade Gir</h1>
                <div className="flex items-center gap-2 mt-0.5">
                   <span className="px-2 py-0.5 bg-orange-50 text-orange-600 text-[9px] font-black rounded-lg uppercase border border-orange-100">YENİ İADE</span>
                   <span className="text-slate-400 text-xs font-medium">• Stok çıkışı ve cari düşümü</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center md:justify-end gap-4">
            <div className="flex items-center gap-4 px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl shadow-inner">
              <div className="flex flex-col">
                <span className="text-[9px] text-slate-400 font-black uppercase tracking-widest mb-0.5">Tedarikçi Firma</span>
                <span className="font-black text-slate-800 tracking-tight leading-none text-sm">{supplier.name}</span>
              </div>
              <div className="w-px h-6 bg-slate-200"></div>
              <div className="flex flex-col">
                <span className="text-[9px] text-slate-400 font-black uppercase tracking-widest mb-0.5">Cari Bakiye</span>
                <div className="flex items-center gap-2 leading-none">
                  <span className={`font-black text-base ${supplier.balance >= 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {supplier.balance.toLocaleString("tr-TR")} ₺
                  </span>
                </div>
              </div>
            </div>
            <Button
              label="İADEYİ KAYDET"
              icon="pi pi-save"
              className="!bg-orange-600 border-none hover:!bg-orange-700 rounded-xl px-8 py-3.5 font-black text-sm shadow-lg !shadow-orange-600/20 transition-all hover:scale-[1.02]"
              onClick={handleSave}
              disabled={lines.length === 0 || isSubmitting}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
          <div className="lg:col-span-2 xl:col-span-2">
            <div className="bg-slate-50/50 border border-slate-200 rounded-2xl p-4 space-y-4">
              <h2 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] flex items-center gap-2 mb-2">
                <i className="pi pi-file-edit text-orange-600"></i> Evrak Bilgileri
              </h2>

              <div className="space-y-3.5">
                <div className="space-y-1">
                  <label className="text-[9px] text-slate-400 font-black uppercase tracking-wider block ml-1">İade Fatura No</label>
                  <InputText
                    value={returnHeader.invoiceNo}
                    onChange={(e) => handleHeaderChange("invoiceNo", e.target.value)}
                    className="w-full border-slate-200 rounded-xl focus:border-orange-500 py-2.5 shadow-sm font-bold text-sm"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] text-slate-400 font-black uppercase tracking-wider block ml-1">İade Tarihi</label>
                  <Calendar
                    value={returnHeader.returnDate}
                    onChange={(e) => handleHeaderChange("returnDate", e.value || null)}
                    showIcon
                    appendTo={document.body}
                    dateFormat="dd.mm.yy"
                    locale="tr"
                    className="w-full"
                    inputClassName="rounded-xl border-slate-200 py-2.5 font-bold"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] text-slate-400 font-black uppercase tracking-wider block ml-1">Depo Seçimi</label>
                  <Dropdown
                    value={returnHeader.warehouseId}
                    onChange={(e) => {
                      const selected = warehouseOptions.find(o => o.value === e.value);
                      handleHeaderChange("warehouseId", e.value);
                      handleHeaderChange("warehouseCode", selected ? selected.label : "");
                    }}
                    options={warehouseOptions}
                    placeholder="Depo Seçiniz"
                    className="w-full rounded-xl border-slate-200 py-0.5 shadow-sm font-bold"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] text-slate-400 font-black uppercase tracking-wider block ml-1">İşlem Açıklaması</label>
                  <InputText
                    value={returnHeader.description}
                    onChange={(e) => handleHeaderChange("description", e.target.value)}
                    placeholder="İade nedeni..."
                    className="w-full border-slate-200 rounded-xl focus:border-orange-500 py-3 shadow-sm font-bold"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8 xl:col-span-8">
            <div className="bg-white border border-slate-200 rounded-2xl p-2 md:p-2 space-y-6 relative shadow-sm">
              <div className="flex items-center justify-between">
                <h2 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] flex items-center gap-2">
                  <i className="pi pi-box text-orange-600"></i> Ürün ve Fiyat Girişi
                </h2>
              </div>

              <div className="flex flex-col gap-4 pb-2">
                <div className="flex flex-col gap-1.5" style={{ position: "relative", zIndex: 10 }}>
                  <label className="text-[9px] text-slate-400 font-black uppercase tracking-wider block ml-1">Ürün Arama (En az 3 karakter)</label>
                  <div className="relative">
                    <i className="pi pi-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 z-10 text-sm"></i>
                    <InputText
                      ref={inputRef}
                      value={productQuery}
                      placeholder="Ürün adı veya barkod yazınız..."
                      className="w-full !pl-10 !py-3 border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-500 shadow-sm font-black text-base"
                      onChange={(e) => {
                        const value = e.target.value;
                        setProductQuery(value);
                        if (value.length >= 3) searchProducts(value);
                        else { setProducts([]); setShowList(false); }
                      }}
                      onFocus={() => { if (productQuery.length >= 3 && products.length) setShowList(true); }}
                      onKeyDown={(e) => {
                        if (!["ArrowDown", "ArrowUp", "Enter", "Escape"].includes(e.key)) return;
                        if (!products.length) return;
                        if (e.key === "ArrowDown") setHighlightIndex((prev) => Math.min(prev + 1, products.length - 1));
                        if (e.key === "ArrowUp") setHighlightIndex((prev) => Math.max(prev - 1, 0));
                        if (e.key === "Enter") {
                          if (!showList || !products.length) return;
                          e.preventDefault();
                          selectProduct(products[highlightIndex]);
                        }
                        if (e.key === "Escape") {
                          e.preventDefault(); setProductQuery(""); setProducts([]); setHighlightIndex(0); setShowList(false);
                        }
                      }}
                    />
                  </div>

                  {showList && products.length > 0 && (
                    <div style={{ zIndex: 9999 }} className="absolute top-[100%] left-0 right-0 mt-2 bg-white shadow-2xl rounded-2xl border border-slate-100 max-h-[320px] overflow-y-auto overflow-x-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                      {products.map((p, i) => (
                        <div
                          key={p.id}
                          onMouseDown={() => selectProduct(p)}
                          className={`flex items-center gap-3 px-5 py-4 cursor-pointer transition-all ${
                            i === highlightIndex ? "!bg-orange-600 text-white shadow-lg shadow-orange-600/20 translate-x-2" : "hover:bg-slate-50 text-slate-700"
                          }`}
                        >
                          <i className={`pi pi-box ${i === highlightIndex ? '!text-white' : '!text-orange-500'}`}></i>
                          <div className="flex flex-col">
                            <span className="font-black text-sm">{p.name}</span>
                            <span className={`text-[10px] ${i === highlightIndex ? '!text-orange-100' : 'text-slate-400'}`}>Fiyat: {p.defaultPurchasePrice?.toLocaleString()} ₺ • KDV: %{p.vatRate}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-1 bg-slate-50/50 p-3 rounded-xl border border-slate-100">
                  <div className="space-y-1">
                    <label className="text-[9px] text-slate-400 font-black uppercase tracking-wider block ml-1">İade Adedi</label>
                    <InputNumber
                      ref={quantityRef}
                      value={newLine.quantity}
                      onValueChange={(e) => handleNewLineChange("quantity", e.value || 0)}
                      min={1}
                      className="w-full"
                      inputClassName="w-full rounded-lg border-slate-200 py-2 font-black text-center text-base"
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); focusAndSelect(priceRef); } }}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] text-slate-400 font-black uppercase tracking-wider block ml-1">Birim Fiyat</label>
                    <InputNumber
                      ref={priceRef}
                      value={newLine.unitPrice}
                      onValueChange={(e) => handleNewLineChange("unitPrice", e.value || 0)}
                      mode="currency"
                      currency="TRY"
                      locale="tr-TR"
                      className="w-full"
                      inputClassName="w-full rounded-xl border-slate-200 py-2 font-black text-center text-base"
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); focusAndSelect(discountRef); } }}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] text-slate-400 font-black uppercase tracking-wider block ml-1">İskonto (%)</label>
                    <InputNumber
                      ref={discountRef}
                      value={newLine.discountRate}
                      onValueChange={(e) => handleNewLineChange("discountRate", e.value || 0)}
                      suffix="%"
                      min={0}
                      max={100}
                      className="w-full"
                      inputClassName="w-full rounded-xl border-slate-200 py-2 font-black text-center text-base text-red-600"
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); focusAndSelect(vatRef); } }}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] text-slate-400 font-black uppercase tracking-wider block ml-1">KDV (%)</label>
                    <InputNumber
                      ref={vatRef}
                      value={newLine.vatRate}
                      onValueChange={(e) => handleNewLineChange("vatRate", e.value || 0)}
                      suffix="%"
                      className="w-full"
                      inputClassName="w-full rounded-xl border-slate-200 py-2 font-black text-center text-base text-blue-600"
                    />
                  </div>

                  <div className="flex flex-col justify-end gap-2">
                    <div className="flex items-center gap-2 mb-1 px-2">
                      <Checkbox
                        checked={!!newLine.unitPriceIsVatIncluded}
                        onChange={(e) => handleNewLineChange("unitPriceIsVatIncluded", e.checked)}
                        inputId="vatIncludedCheck"
                      />
                      <label htmlFor="vatIncludedCheck" className="text-[9px] font-black text-slate-500 uppercase tracking-tighter cursor-pointer select-none">KDV Dahil</label>
                    </div>
                    <Button
                      label="EKLE"
                      icon="pi pi-plus"
                      className="!bg-orange-600 border-none hover:!bg-orange-700 rounded-xl px-6 py-4 font-black text-white shadow-lg shadow-orange-500/20 w-full transition-all active:scale-95"
                      onClick={handleAddLine}
                      disabled={!newLine.product}
                    />
                  </div>
                </div>
              </div>

              <DataTable
                value={lines}
                size="small"
                className="p-datatable-sm"
                emptyMessage={<div className="text-center py-6 text-slate-400 font-bold">Henüz iade edilecek ürün eklenmedi</div>}
              >
                <Column field="productName" header="Ürün" className="py-3 px-4 font-bold text-slate-700" />
                <Column field="quantity" header="Miktar" body={(r) => `${r.quantity} ${r.unit}`} />
                <Column field="unitPrice" header="Birim Fiyat" body={(r) => `₺${r.unitPrice?.toLocaleString()}`} />
                <Column field="discountRate" header="İsk" body={(r) => r.discountRate > 0 ? <span className="text-red-500">%{r.discountRate}</span> : '-'} />
                <Column field="vatRate" header="KDV" body={(r) => <span className="text-blue-500">%{r.vatRate}</span>} />
                <Column
                  header="Toplam"
                  body={(rowData) => {
                    const qty = rowData.quantity || 0;
                    const unitPrice = rowData.unitPrice || 0;
                    const discountRate = rowData.discountRate || 0;
                    const vatRate = rowData.vatRate || 0;
                    const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;

                    let total = 0;
                    if (rowData.unitPriceIsVatIncluded) {
                      const unitPriceExcl = vatRate > 0 ? unitPrice / (1 + vatRate / 100) : unitPrice;
                      const grossExcl = unitPriceExcl * qty;
                      const discountExcl = round2(grossExcl * (discountRate / 100));
                      const netExclAfterDiscount = round2(grossExcl - discountExcl);
                      const vat = round2(netExclAfterDiscount * (vatRate / 100));
                      total = round2(netExclAfterDiscount + vat);
                    } else {
                      const gross = qty * unitPrice;
                      const discount = gross * (discountRate / 100);
                      const net = gross - discount;
                      const vat = net * (vatRate / 100);
                      total = net + vat;
                    }
                    return (
                      <span className="font-black text-slate-800">
                        ₺{total.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    );
                  }}
                  style={{ textAlign: "right" }}
                />
                <Column
                  body={(rowData) => (
                    <Button
                      icon="pi pi-trash"
                      className="p-button-rounded p-button-text p-button-danger"
                      onClick={(e) => openDeleteConfirm(e, rowData.id)}
                    />
                  )}
                />
              </DataTable>
            </div>
          </div>

          <div className="lg:col-span-2 xl:col-span-2 h-full">
            <div className="bg-slate-900 text-slate-50 rounded-2xl p-5 space-y-6 h-full shadow-xl shadow-slate-900/30 relative overflow-hidden flex flex-col justify-between">
              <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>

              <div className="space-y-6 relative z-10">
                <h2 className="text-[10px] font-black text-orange-400 uppercase tracking-[0.3em] flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse"></div>
                  İade Özeti
                </h2>

                <div className="space-y-3.5 relative z-10">
                  <div className="flex items-center justify-between text-xs opacity-60">
                    <span className="font-bold uppercase tracking-tighter">Ara Toplam</span>
                    <span className="font-black text-white">{totals.gross.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-red-400 font-bold">
                    <span className="uppercase tracking-tighter">İskonto</span>
                    <span className="font-black">-{totals.discountTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-blue-400 font-bold">
                    <span className="uppercase tracking-tighter">KDV</span>
                    <span className="font-black">+{totals.vatTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</span>
                  </div>

                  <div className="pt-4 border-t border-slate-800 space-y-1.5">
                    <span className="text-[9px] font-black text-slate-500 uppercase tracking-[0.2em] block">DÜŞÜLECEK TOPLAM TUTAR</span>
                    <div className="text-3xl font-black text-white tracking-tight">
                      {totals.netTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} <span className="text-lg text-orange-500 ml-0.5">₺</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3 relative z-10">
                 <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/5 backdrop-blur-md p-3 rounded-xl border border-white/10 text-center group">
                    <span className="text-[9px] block text-slate-500 mb-0.5 uppercase font-black tracking-widest">KALEM</span>
                    <span className="font-black text-lg text-white">{totals.lineCount}</span>
                  </div>
                  <div className="bg-white/5 backdrop-blur-md p-3 rounded-xl border border-white/10 text-center group">
                    <span className="text-[9px] block text-slate-500 mb-0.5 uppercase font-black tracking-widest">ADET</span>
                    <span className="font-black text-lg text-white">{totals.totalQuantity}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
